import { useState, useEffect } from "react";
import { auth, db } from "../firebase";
import { collection, query, where, getDocs, doc, deleteDoc } from "firebase/firestore";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";

const ViewListings = ({ onEditListing }) => {
  console.log("ViewListings rendered");
  const [listings, setListings] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const listingsPerPage = 5;

  useEffect(() => {
    if (!auth.currentUser) return;

    const fetchData = async () => {
      try {
        const listingQuery = query(collection(db, "listings"), where("userId", "==", auth.currentUser.uid));
        const listingSnapshot = await getDocs(listingQuery);
        const listingsData = listingSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        const campaignQuery = query(collection(db, "campaigns"), where("userId", "==", auth.currentUser.uid));
        const campaignSnapshot = await getDocs(campaignQuery);
        const campaignsData = campaignSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

        setListings(listingsData);
        setCampaigns(campaignsData);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, []);

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const items = [...listings];
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    setListings(items);
  };

  const handleDelete = async (listingId) => {
    if (!window.confirm("Are you sure you want to delete this listing?")) return;

    try {
      await deleteDoc(doc(db, "listings", listingId));
      setListings(listings.filter(listing => listing.id !== listingId));
    } catch (error) {
      console.error("Error deleting listing:", error);
    }
  };

  const handleEdit = (listingId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    console.log("Edit button clicked for listing:", listingId);
    // Use the callback function provided by Dashboard
    onEditListing(listingId);
  };

  const filteredListings = listings.filter(listing =>
    (selectedCampaign === "all" || listing.campaignId === selectedCampaign) &&
    (listing.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      listing.category?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const totalPages = Math.ceil(filteredListings.length / listingsPerPage);
  const indexOfLastListing = currentPage * listingsPerPage;
  const indexOfFirstListing = indexOfLastListing - listingsPerPage;
  const currentListings = filteredListings.slice(indexOfFirstListing, indexOfLastListing);

  return (
    <div className="view-listings-wrapper">
      <h2>Your Listings</h2>

      <div className="search-filter-container">
        <div className="search-bar">
          <label>Search:</label>
          <input
            type="text"
            placeholder="Search listings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="filter-dropdown">
          <label>Filter by Campaign:</label>
          <select value={selectedCampaign} onChange={(e) => setSelectedCampaign(e.target.value)}>
            <option value="all">All Campaigns</option>
            {campaigns.map(campaign => (
              <option key={campaign.id} value={campaign.id}>{campaign.name}</option>
            ))}
          </select>
        </div>
      </div>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="listings">
          {(provided) => (
            <table className="listing-table" {...provided.droppableProps} ref={provided.innerRef}>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Price</th>
                  <th>Photo</th>
                  <th>Location</th>
                  <th>Campaign</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentListings.map((listing, index) => (
                  <Draggable key={listing.id} draggableId={listing.id} index={index}>
                    {(provided) => (
                      <tr ref={provided.innerRef} {...provided.draggableProps} {...provided.dragHandleProps}>
                        <td>{listing.title}</td>
                        <td>${listing.price}</td>
                        <td>
                          {listing.photos?.length > 0 ? (
                            <img src={listing.photos[0]} alt="Listing" className="listing-thumbnail" />
                          ) : (
                            "No Image"
                          )}
                        </td>
                        <td>{listing.location}</td>
                        <td>{campaigns.find(campaign => campaign.id === listing.campaignId)?.name || "Unassigned"}</td>
                        <td className="action-buttons">
                          <button 
                            className="edit-button" 
                            onClick={(event) => handleEdit(listing.id, event)}
                          >✏️ Edit</button>
                          <button 
                            className="delete-button" 
                            onClick={() => handleDelete(listing.id)}
                          >🗑️ Delete</button>
                        </td>
                      </tr>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </tbody>
            </table>
          )}
        </Droppable>
      </DragDropContext>

      <div className="pagination">
        <button 
          disabled={currentPage === 1} 
          onClick={() => setCurrentPage(currentPage - 1)}
          className="pagination-button"
        >
          Previous
        </button>

        <span className="pagination-text">Page {currentPage} of {totalPages}</span>

        <button 
          disabled={currentPage >= totalPages} 
          onClick={() => setCurrentPage(currentPage + 1)}
          className="pagination-button"
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default ViewListings;